// ============================================================================
// lib/site/visibility.ts — ВИДИМОСТЬ БЛОКОВ/СТРАНИЦ САЙТА (Этап 3):
// '' / 'all' — всем; 'desktop' / 'mobile' — устройство; 'auth' — только
// авторизованным; 'guest' — только анонимам; role:<код> — роли пользователя
// (JWT, без admin-дефолтов). Единый источник — используется блоками
// (AppearanceWrap) и страницей (PublicSitePage).
// ============================================================================

import { useCenterStore } from '@/store/centerStore';
import { decodeJwtPayload, jwtNotExpired } from '@/lib/jwt';

/** currentUserRoles — роли текущего пользователя из JWT (пусто — аноним). */
export function currentUserRoles(): string[] {
  const token = useCenterStore.getState().token || '';
  if (!token || !jwtNotExpired(token)) return [];
  const payload = decodeJwtPayload(token);
  const roles = payload?.roles;
  return Array.isArray(roles) ? roles.map(String) : [];
}

/** isSiteAuthenticated — есть действующий JWT (вход выполнен). */
export function isSiteAuthenticated(): boolean {
  const token = useCenterStore.getState().token || '';
  return !!token && jwtNotExpired(token);
}

/** roleVisibility — 'role:<код>' → код роли ('' — не ролевое значение). */
export function roleVisibility(value: string): string {
  const s = String(value || '').trim();
  if (!s) return '';
  if (s.startsWith('role:')) return s.slice(5).trim();
  // Обратная совместимость: значение — просто код роли (не device/all/auth/guest).
  if (['all', 'desktop', 'mobile', 'auth', 'guest'].includes(s)) return '';
  return s;
}

/**
 * visibilityAllows — разрешена ли видимость для текущего зрителя.
 * visibility: '' | 'all' | 'desktop' | 'mobile' | 'auth' | 'guest' | 'role:<код>'.
 * isMobile — устройство (для desktop/mobile); roleCode — отдельное поле роли.
 */
export function visibilityAllows(visibility: string, roleCode: string, isMobile: boolean): boolean {
  const vis = String(visibility || '').trim();
  const role = roleCode || roleVisibility(vis);
  if (vis === 'auth' && !isSiteAuthenticated()) return false;
  if (vis === 'guest' && isSiteAuthenticated()) return false;
  if (vis === 'desktop' && isMobile) return false;
  if (vis === 'mobile' && !isMobile) return false;
  if (role) {
    const roles = currentUserRoles();
    if (!roles.includes(role)) return false;
  }
  return true;
}

/** needsCurrentUserFilter — отбор блока использует @current_user (кабинет). */
export function needsCurrentUserFilter(filters: unknown): boolean {
  const raw = typeof filters === 'string' ? filters : JSON.stringify(filters || []);
  return raw.includes('@current_user');
}
